import React, { useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { ProjectWithStats } from '../../../../atoms/v3/projectV2Atoms';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { getProjectColor } from '../../../../utils/colorUtils';

interface ProjectItemV2Props {
  project: ProjectWithStats;
  onClick: (projectId: string) => void;
  showStats?: boolean; // 是否显示统计信息
  showQuickAddButton?: boolean; // 是否显示快速添加按钮
  onQuickAddClick?: (projectId: string) => void; // 快速添加按钮点击回调
}

/**
 * ProjectV2 基础项目项组件
 * 参考 TaskItemV2 的设计风格，采用无边界设计
 */
export const ProjectItemV2: React.FC<ProjectItemV2Props> = ({
  project,
  onClick,
  showStats = true,
  showQuickAddButton = false,
  onQuickAddClick
}) => {
  const { t } = useTranslation(['v3/projects', 'v3/tasks']);
  const [isDark] = useAtom(isDarkAtom);

  // 处理项目点击
  const handleClick = useCallback(() => {
    onClick(project.id);
  }, [onClick, project.id]);

  // 处理快速添加按钮点击
  const handleQuickAddClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // 防止触发项目点击事件
    onQuickAddClick?.(project.id);
  }, [onQuickAddClick, project.id]);

  // 获取项目颜色（使用现有的颜色系统）
  const projectColor = useMemo(() => {
    return getProjectColor(project.id);
  }, [project.id]);

  // 格式化完成率
  const completionRateText = useMemo(() => {
    const { stats } = project;
    if (stats.totalTasks === 0) return t('v3/projects:stats.noTasks');
    return t('v3/projects:stats.completionRate', { 
      completed: stats.completedTasks, 
      total: stats.totalTasks,
      rate: stats.completionRate 
    });
  }, [project.stats, t]);

  // 重要性统计文本
  const importanceStats = useMemo(() => {
    const { stats } = project;

    // 计算总的重要+紧急任务数
    const totalImportantTasks = stats.vitalTasks + stats.urgentTasks;

    // 永远显示总数前缀（灰色）
    const totalText = t('v3/projects:stats.totalPrefix', { count: totalImportantTasks });

    // 重要和紧急任务的彩色部分
    const coloredParts = [];
    if (stats.vitalTasks > 0) {
      coloredParts.push(t('v3/projects:stats.vital', { count: stats.vitalTasks }));
    }
    if (stats.urgentTasks > 0) {
      coloredParts.push(t('v3/projects:stats.urgent', { count: stats.urgentTasks }));
    }

    return {
      totalText,
      coloredText: coloredParts.length > 0 ? coloredParts.join(', ') : null
    };
  }, [project.stats, t]);

  return (
    <div
      className={`
        flex px-4 py-3 cursor-pointer transition-all duration-200 items-center
        ${isDark
          ? 'bg-ios-dark-gray6 hover:bg-ios-dark-gray5'
          : 'bg-white hover:bg-gray-50'
        }
      `}
      onClick={handleClick}
    >
      {/* 左侧：项目颜色标识 */}
      <div
        className="w-4 h-4 rounded-full mr-3"
        style={{ backgroundColor: projectColor }}
      ></div>

      {/* 中间：项目信息 */}
      <div className="flex-1 min-w-0">
        {/* 项目标题 */}
        <div className={`text-ios-body font-medium truncate ${
          isDark ? 'text-ios-dark-gray1' : 'text-ios-gray1'
        }`}>
          {project.title}
        </div>

        {/* 统计信息和项目描述 - 合并在同一行 */}
        {(showStats || project.context) && (
          <div className={`text-ios-caption mt-1 truncate ${isDark ? 'text-ios-dark-gray2' : 'text-ios-gray2'}`}>
            {/* 重要性统计 */}
            {showStats && (
              <>
                {/* 总数（永远显示，灰色） */}
                <span className={`${isDark ? 'text-ios-dark-gray3' : 'text-ios-gray3'}`}>
                  {importanceStats.totalText}
                </span>
                {/* 重要/紧急任务（橙色，有时显示） */}
                {importanceStats.coloredText && (
                  <>
                    <span className={`${isDark ? 'text-ios-dark-gray3' : 'text-ios-gray3'}`}>, </span>
                    <span className={`${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                      {importanceStats.coloredText}
                    </span>
                  </>
                )}
                {/* 如果有描述，添加分隔符 */}
                {project.context && (
                  <span className={`${isDark ? 'text-ios-dark-gray3' : 'text-ios-gray3'}`}> · </span>
                )}
              </>
            )}
            {/* 项目描述 */}
            {project.context && (
              <span>{project.context}</span>
            )}
          </div>
        )}


      </div>

      {/* 右侧：统计信息 */}
      {showStats && (
        <div className="flex flex-col items-end ml-3">
          {/* 完成率 */}
          <div className={`text-ios-caption ${
            isDark ? 'text-ios-dark-gray2' : 'text-ios-gray2'
          }`}>
            {completionRateText}
          </div>

          {/* 完成率进度条 */}
          {project.stats.totalTasks > 0 && (
            <div className="w-16 h-1 bg-gray-200 dark:bg-gray-700 rounded-full mt-1 overflow-hidden">
              <div 
                className="h-full bg-ios-blue dark:bg-ios-dark-blue transition-all duration-300"
                style={{ width: `${project.stats.completionRate}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* 右侧：快速添加按钮和箭头指示器 */}
      <div className="flex items-center ml-2">
        {/* 快速添加按钮 */}
        {showQuickAddButton && (
          <button
            onClick={handleQuickAddClick}
            className={`
              w-6 h-6 rounded-full flex items-center justify-center mr-2 transition-all duration-200
              ${isDark
                ? 'bg-ios-dark-gray4 hover:bg-ios-dark-gray3 text-ios-dark-gray1'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
              }
              hover:scale-105 active:scale-95
            `}
            title="快速添加任务"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
        )}

        {/* 箭头指示器 */}
        <div className={`${
          isDark ? 'text-ios-dark-gray4' : 'text-ios-gray4'
        }`}>
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};
